import { forwardRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, useColorMode } from '@chakra-ui/react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import imageCompressor from 'quill-image-compress';
import { calculateContentSize } from 'utils/react-quill/quillConfig';
import RichTextSizeIndicator from 'utils/react-quill/RichTextSizeIndicator';
import getQuillStyles from 'utils/react-quill/quillStyling';

// Register the image compression module
ReactQuill.Quill.register('modules/imageCompress', imageCompressor);

/**
 * Reusable ReactQuill editor component with consistent configuration
 * Provides rich text editing capabilities with image compression
 */
const ReactQuillEditor = forwardRef(
  (
    {
      value,
      onChange,
      onBlur,
      placeholder = 'Enter text...',
      readOnly = false,
      showSizeIndicator = true,
      maxSizeInMB = 5,
      ...props
    },
    ref
  ) => {
    const [contentSize, setContentSize] = useState(0);
    const { colorMode } = useColorMode();

    /**
     * Calculate and update content size whenever value changes
     */
    useEffect(() => {
      if (value && showSizeIndicator) {
        const newContentSize = calculateContentSize(value);
        setContentSize(newContentSize);
      } else {
        setContentSize(0);
      }
    }, [value, showSizeIndicator]);

    /**
     * Enhanced onChange handler that includes size calculation
     */
    const handleChange = (content) => {
      if (onChange) {
        onChange(content);
      }

      if (showSizeIndicator) {
        const newContentSize = calculateContentSize(content);
        setContentSize(newContentSize);
      }
    };
    // Fixed modules configuration for consistent editing experience
    const modules = {
      toolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['image'],
        ['clean'],
      ],
      imageCompress: {
        quality: 0.7,
        maxWidth: 1200,
        maxHeight: 800,
        imageType: 'image/jpeg',
        debug: false,
        suppressErrorLogging: false,
        handleOnPaste: true,
        maxFileSize: maxSizeInMB * 1024 * 1024,
      },
    };

    // Fixed formats configuration
    const formats = [
      'bold',
      'italic',
      'underline',
      'list',
      'bullet',
      'link',
      'image',
    ];

    // Get dark mode styles
    const style = getQuillStyles(colorMode);

    return (
      <Box position="relative" sx={style} {...props}>
        <ReactQuill
          ref={ref}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={placeholder}
          readOnly={readOnly}
          theme="snow"
          modules={modules}
          formats={formats}
          style={{
            borderRadius: '6px',
            minHeight: '150px',
            backgroundColor: colorMode === 'dark' ? '#2D3748' : '#ffffff',
          }}
        />
        {showSizeIndicator && (
          <RichTextSizeIndicator size={contentSize} maxSize={maxSizeInMB} />
        )}
      </Box>
    );
  }
);

ReactQuillEditor.displayName = 'ReactQuillEditor';

ReactQuillEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  showSizeIndicator: PropTypes.bool,
  maxSizeInMB: PropTypes.number,
};

ReactQuillEditor.defaultProps = {
  value: '',
  onChange: undefined,
  onBlur: undefined,
  placeholder: 'Enter text...',
  readOnly: false,
  showSizeIndicator: true,
  maxSizeInMB: 5,
};

export default ReactQuillEditor;
